import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Menu, X, LogOut, ChevronDown } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = React.useState(false);

    const dashLink =
        user && user.role === "creator"
            ? "/creator"
            : user && user.role === "brand"
              ? "/brand"
              : user && user.role === "admin"
                ? "/admin"
                : null;

    const onLogout = async () => {
        await logout();
        navigate("/");
    };

    return (
        <header
            className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b hairline"
            data-testid="navbar"
        >
            <div className="max-w-[1280px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
                    <div className="w-8 h-8 rounded-full bg-coral flex items-center justify-center text-white font-display font-bold text-lg">
                        c
                    </div>
                    <span className="font-display text-xl font-semibold text-slate-900">
                        castline
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-600">
                    {user && dashLink && (
                        <Link to={dashLink} className="hover:text-slate-900" data-testid="nav-dashboard">
                            Dashboard
                        </Link>
                    )}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    {user ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    className="btn-pill-ghost flex items-center gap-2"
                                    data-testid="nav-user"
                                >
                                    <span className="w-6 h-6 rounded-full bg-lime text-lime-ink flex items-center justify-center text-xs font-bold">
                                        {user.name?.[0]?.toUpperCase()}
                                    </span>
                                    {user.name?.split(" ")[0]}
                                    <ChevronDown className="w-4 h-4" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                className="rounded-xl bg-white border-slate-200 shadow-lg w-56"
                            >
                                <div className="px-3 py-2 text-xs text-slate-500">
                                    Signed in as <span className="text-slate-900 font-semibold">{user.email}</span>
                                </div>
                                <DropdownMenuSeparator />
                                {dashLink && (
                                    <DropdownMenuItem
                                        onClick={() => navigate(dashLink)}
                                        className="cursor-pointer"
                                        data-testid="menu-dashboard"
                                    >
                                        Dashboard
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                    onClick={onLogout}
                                    className="cursor-pointer text-red-600"
                                    data-testid="nav-logout"
                                >
                                    <LogOut className="w-4 h-4 mr-2" /> Log out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <>
                            <Link to="/login">
                                <button className="btn-pill-ghost" data-testid="nav-login">
                                    Log in
                                </button>
                            </Link>
                            <Link to="/register">
                                <button className="btn-pill-primary" data-testid="nav-signup">
                                    Get started
                                </button>
                            </Link>
                        </>
                    )}
                </div>

                <button
                    className="md:hidden text-slate-900"
                    onClick={() => setOpen(!open)}
                    data-testid="nav-mobile-toggle"
                    aria-label="Menu"
                >
                    {open ? <X /> : <Menu />}
                </button>
            </div>

            {open && (
                <div className="md:hidden border-t hairline px-5 py-5 bg-white space-y-4">
                    {user ? (
                        <>
                            {dashLink && (
                                <Link
                                    to={dashLink}
                                    onClick={() => setOpen(false)}
                                    className="block text-sm font-medium text-slate-700"
                                >
                                    Dashboard
                                </Link>
                            )}
                            <button onClick={onLogout} className="btn-pill-primary w-full">
                                Log out
                            </button>
                        </>
                    ) : (
                        <div className="flex gap-3">
                            <Link to="/login" className="flex-1" onClick={() => setOpen(false)}>
                                <button className="btn-pill-ghost w-full">Log in</button>
                            </Link>
                            <Link to="/register" className="flex-1" onClick={() => setOpen(false)}>
                                <button className="btn-pill-primary w-full">Get started</button>
                            </Link>
                        </div>
                    )}
                </div>
            )}
        </header>
    );
}
